import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

// shadcn utility for merging Tailwind classes
export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

// ============================================
// FORMATTING UTILITIES
// ============================================

export function formatMoney(amount: number): string {
    const absAmount = Math.abs(amount);

    if (absAmount >= 1000000) {
        return `$${(amount / 1000000).toFixed(2)}M`;
    }
    if (absAmount >= 1000) {
        return `$${(amount / 1000).toFixed(1)}k`;
    }
    return `$${amount.toFixed(0)}`;
}

export function formatPercent(value: number): string {
    return `${(value * 100).toFixed(0)}%`;
}

export function formatWalletAddress(address: string): string {
    if (address.length <= 13) return address;
    return `${address.slice(0, 6)}…${address.slice(-4)}`;
}

export function formatTimeAgo(timestamp: number): string {
    const now = Date.now() / 1000;
    const diff = now - timestamp;

    if (diff < 60) {
        return 'Just now';
    }
    if (diff < 3600) {
        const mins = Math.floor(diff / 60);
        return `${mins}m ago`;
    }
    if (diff < 86400) {
        const hours = Math.floor(diff / 3600);
        return `${hours}h ago`;
    }
    if (diff < 604800) {
        const days = Math.floor(diff / 86400);
        return `${days}d ago`;
    }

    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
    });
}

export function formatDate(timestamp: number): string {
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

export function formatTimeToResolution(endDate: number | null): string {
    if (!endDate) return 'Unknown';

    const now = Date.now() / 1000;
    const diff = endDate - now;

    if (diff <= 0) {
        return 'Ended';
    }
    if (diff < 3600) {
        const mins = Math.floor(diff / 60);
        return `${mins} min`;
    }
    if (diff < 86400) {
        const hours = Math.floor(diff / 3600);
        return `${hours} hours`;
    }
    const days = Math.floor(diff / 86400);
    return `${days} days`;
}

// ============================================
// SCORE UTILITIES
// ============================================

export function getSeverityVariant(score: number): 'critical' | 'high' | 'medium' | 'low' {
    if (score >= 80) return 'critical';
    if (score >= 60) return 'high';
    if (score >= 40) return 'medium';
    return 'low';
}

export function getSeverityEmoji(severity: string): string {
    switch (severity) {
        case 'critical': return '🚨';
        case 'high': return '🟠';
        case 'medium': return '🟢';
        default: return '⚪';
    }
}

export function getSeverityLabel(severity: string): string {
    switch (severity) {
        case 'critical': return 'Red Alert';
        case 'high': return 'Whale Move';
        case 'medium': return 'Interesting';
        default: return 'Signal';
    }
}
